export class ApiClientError extends Error {
    status;
    code;
    details;
    /** Set when the server refused a scan but still knows which mould it was. */
    mould;
    /** The server's reference for a failure, when it gave one. */
    ref;
    constructor(status, body) {
        super(body.error?.message ?? `HTTP ${status}`);
        this.name = 'ApiClientError';
        this.status = status;
        this.code = body.error?.code ?? 'UNKNOWN';
        this.details = body.error?.details;
        this.mould = body.error?.mould;
        this.ref = body.error?.ref;
    }
}
/**
 * Read a response as JSON, or say plainly why it is not.
 *
 * Not every error arrives as JSON. A proxy refusing a request answers with its
 * own HTML page - "413 Request Entity Too Large" - and parsing that threw a
 * SyntaxError, which nothing downstream recognised as a server answer. The
 * request then looked like a mysterious crash instead of what it was.
 */
async function readJson(res) {
    const text = await res.text();
    let json;
    try {
        json = text ? JSON.parse(text) : undefined;
    }
    catch {
        const title = /<title>([^<]*)<\/title>/i.exec(text)?.[1]?.trim();
        throw new ApiClientError(res.status, {
            error: {
                code: res.status === 413 ? 'PAYLOAD_TOO_LARGE' : 'BAD_RESPONSE',
                message: res.status === 413
                    ? 'The server refused this request because it is too large.'
                    : `The server answered with something that is not data (${title || `HTTP ${res.status}`}).`,
            },
        });
    }
    if (!res.ok)
        throw new ApiClientError(res.status, (json ?? {}));
    return json;
}
/**
 * Every request gets a deadline.
 *
 * A network that fails is easy; a network that *hangs* is what breaks things. A
 * captive portal, a proxy black-holing traffic, or a stalled mobile connection
 * accepts the socket and then says nothing, so `fetch` neither resolves nor
 * rejects. The sync engine guards against overlapping runs with a single
 * in-flight promise, so one hung request meant that promise never settled and
 * every later sync returned the same dead promise - the app sat on "Offline"
 * with unsent edits until somebody reloaded the page. Observed, not theorised.
 *
 * Long enough for a slow upload on a bad connection, short enough that a wedged
 * request resolves itself well inside the sync engine's poll interval.
 */
const DEFAULT_TIMEOUT_MS = 30_000;
export class ApiClient {
    baseUrl;
    getAccessToken;
    getRefreshToken;
    onTokensRefreshed;
    onAuthFailure;
    refreshPromise = null;
    authFailed = false;
    timeoutMs;
    constructor(opts) {
        this.baseUrl = opts.baseUrl.replace(/\/$/, '');
        this.getAccessToken = opts.getAccessToken ?? (() => null);
        this.getRefreshToken = opts.getRefreshToken ?? (() => null);
        this.onTokensRefreshed = opts.onTokensRefreshed;
        this.onAuthFailure = opts.onAuthFailure;
        this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    }
    /**
     * A deadline for one request. Falls back to an unlimited signal on the rare
     * runtime without AbortSignal.timeout rather than failing outright.
     */
    deadline(ms = this.timeoutMs) {
        return typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
            ? AbortSignal.timeout(ms)
            : undefined;
    }
    /** Single-flight refresh: concurrent 401s share one /auth/refresh call. */
    async tryRefresh() {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken)
            return null;
        if (!this.refreshPromise) {
            this.refreshPromise = fetch(`${this.baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ refreshToken }),
                signal: this.deadline(),
            })
                .then(async (r) => {
                if (!r.ok)
                    return null;
                const j = (await r.json());
                if (j.accessToken)
                    this.onTokensRefreshed?.(j.accessToken);
                return j.accessToken ?? null;
            })
                .catch(() => null)
                .finally(() => {
                this.refreshPromise = null;
            });
        }
        return this.refreshPromise;
    }
    async request(method, path, body, retry = true, timeoutMs) {
        const headers = { Accept: 'application/json' };
        const token = this.getAccessToken();
        if (token)
            headers.Authorization = `Bearer ${token}`;
        if (body !== undefined)
            headers['Content-Type'] = 'application/json';
        const res = await fetch(`${this.baseUrl}${path}`, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            signal: this.deadline(timeoutMs),
        });
        // Token expired/invalid: try a silent refresh once, then retry the request.
        if (res.status === 401 && retry && !path.startsWith('/auth/')) {
            const fresh = await this.tryRefresh();
            if (fresh)
                return this.request(method, path, body, false, timeoutMs);
            // Unrecoverable - notify once so the app can redirect to login.
            if (!this.authFailed) {
                this.authFailed = true;
                this.onAuthFailure?.();
            }
        }
        if (res.status === 204)
            return undefined;
        return readJson(res);
    }
    /**
     * Send raw bytes - a photo - with the session attached.
     *
     * Not JSON on purpose. A picture inside a JSON body is a third larger and has
     * to be held in memory twice; sent as itself it is one request per picture,
     * each well under any proxy's limit.
     */
    async upload(path, data, contentType, retry = true) {
        const headers = {
            Accept: 'application/json',
            'Content-Type': contentType,
        };
        const token = this.getAccessToken();
        if (token)
            headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${this.baseUrl}${path}`, {
            method: 'POST',
            headers,
            body: data,
            signal: this.deadline(120_000),
        });
        if (res.status === 401 && retry) {
            const fresh = await this.tryRefresh();
            if (fresh)
                return this.upload(path, data, contentType, false);
            if (!this.authFailed) {
                this.authFailed = true;
                this.onAuthFailure?.();
            }
        }
        return readJson(res);
    }
    /** POST with a longer deadline, for work that is slow by nature (an import). */
    postSlow(path, body, timeoutMs) {
        return this.request('POST', path, body, true, timeoutMs);
    }
    /**
     * Authenticated binary download (e.g. nameplate DXF/PDF). Attaches the JWT,
     * refreshes once on 401, and returns the response Blob. A large file (a ZIP
     * of every plate) passes a longer deadline.
     */
    async getBlob(path, retry = true, timeoutMs) {
        const headers = {};
        const token = this.getAccessToken();
        if (token)
            headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${this.baseUrl}${path}`, {
            method: 'GET',
            headers,
            signal: this.deadline(timeoutMs),
        });
        if (res.status === 401 && retry) {
            const fresh = await this.tryRefresh();
            if (fresh)
                return this.getBlob(path, false, timeoutMs);
            if (!this.authFailed) {
                this.authFailed = true;
                this.onAuthFailure?.();
            }
        }
        if (!res.ok) {
            const text = await res.text();
            throw new ApiClientError(res.status, (text ? JSON.parse(text) : {}));
        }
        return res.blob();
    }
    // --- Generic verbs (used by the admin panel for full CRUD) ---
    get(path) {
        return this.request('GET', path);
    }
    post(path, body) {
        return this.request('POST', path, body);
    }
    patch(path, body) {
        return this.request('PATCH', path, body);
    }
    put(path, body) {
        return this.request('PUT', path, body);
    }
    del(path) {
        return this.request('DELETE', path);
    }
    // --- Auth ---
    login(input) {
        return this.request('POST', '/auth/login', input);
    }
    me() {
        return this.request('GET', '/auth/me');
    }
    /** Change your own password. Existing tokens stay valid - see the route. */
    changePassword(input) {
        return this.request('POST', '/auth/change-password', input);
    }
    /** Edit your own profile (name only - see updateProfileSchema). */
    updateProfile(input) {
        return this.request('PATCH', '/auth/me', input);
    }
    // --- Users (admin only) ---
    listUsers() {
        return this.request('GET', '/users');
    }
    createUser(input) {
        return this.request('POST', '/users', input);
    }
    updateUser(id, input) {
        return this.request('PATCH', `/users/${encodeURIComponent(id)}`, input);
    }
    /** Soft delete - the account stops working, its scan history stays. */
    deleteUser(id) {
        return this.request('DELETE', `/users/${encodeURIComponent(id)}`);
    }
    // --- Moulds ---
    listMoulds(query = '') {
        return this.request('GET', `/moulds${query}`);
    }
    getMould(idOrCode) {
        return this.request('GET', `/moulds/${encodeURIComponent(idOrCode)}`);
    }
    getCurrentPosition(idOrCode) {
        return this.request('GET', `/moulds/${encodeURIComponent(idOrCode)}/current-position`);
    }
    // --- Checkpoints ---
    /**
     * Every checkpoint the caller may see. Needs only a signed-in user, so the
     * scanner can offer a list instead of asking someone to remember a code.
     */
    listCheckpoints() {
        return this.request('GET', '/checkpoints');
    }
    // --- Scans ---
    /**
     * A mould's movement record, newest first.
     *
     * Not part of the replicated catalog - the ledger grows without limit and a
     * device has no use for all of it - so this is an online-only read.
     */
    /**
     * One page of a mould's movement history, newest first.
     *
     * Paged rather than capped. A tool scanned twice a day for two years has well
     * over a thousand entries, and returning the newest fifty with nothing to say
     * so presented a truncated history as a complete one.
     */
    listMouldScans(mouldId, limit = 50, page = 1) {
        return this.request('GET', `/scan-events?mouldId=${encodeURIComponent(mouldId)}&limit=${limit}&page=${page}`);
    }
    postScan(input) {
        return this.request('POST', '/scan-events', input);
    }
    /**
     * Flush a queue of scans taken offline.
     *
     * Per-item results, never a wholesale failure: one unknown asset code among
     * fifty good scans must not send the other forty-nine back to the device.
     *
     * `error` is part of the contract, not an afterthought - the route has always
     * returned it and the type used to omit it, which left the only place that
     * could explain a rejection unable to read the explanation.
     */
    postScanBatch(scans) {
        return this.request('POST', '/scan-events/batch', { scans });
    }
    // --- Transfers ---
    /**
     * Movement paperwork: a mould leaving one site for another.
     *
     * Separate from a scan. A scan records that something happened; a transfer
     * says something is *going to* happen and carries the gate pass that lets it
     * off the premises. Scans then attach themselves to the open transfer as the
     * tool passes each checkpoint.
     */
    listTransfers(query = '') {
        return this.request('GET', `/transfers${query}`);
    }
    getTransfer(idOrNo) {
        return this.request('GET', `/transfers/${encodeURIComponent(idOrNo)}`);
    }
    createTransfer(input) {
        return this.request('POST', '/transfers', input);
    }
    updateTransfer(idOrNo, input) {
        return this.request('PATCH', `/transfers/${encodeURIComponent(idOrNo)}`, input);
    }
    // --- journeys ---
    //
    // Online-only, like transfers: a journey is a live claim about where a tool
    // is right now, and a stale local copy of that is worse than none.
    listJourneys(query = '') {
        return this.request('GET', `/journeys${query}`);
    }
    getJourney(id) {
        return this.request('GET', `/journeys/${encodeURIComponent(id)}`);
    }
    createJourney(input) {
        return this.request('POST', '/journeys', input);
    }
    updateJourney(id, action) {
        return this.request('PATCH', `/journeys/${encodeURIComponent(id)}`, { action });
    }
    listRoutes() {
        return this.request('GET', '/journeys/routes');
    }
    createRoute(input) {
        return this.request('POST', '/journeys/routes', input);
    }
    // --- Maintenance (v4) ---
    listJobs(query = '') {
        return this.request('GET', `/maintenance/jobs${query}`);
    }
    createJob(input) {
        return this.request('POST', '/maintenance/jobs', input);
    }
    updateJob(idOrNo, input) {
        return this.request('PATCH', `/maintenance/jobs/${encodeURIComponent(idOrNo)}`, input);
    }
    listSchedules(query = '') {
        return this.request('GET', `/maintenance/schedules${query}`);
    }
    createSchedule(input) {
        return this.request('POST', '/maintenance/schedules', input);
    }
    deleteSchedule(id) {
        return this.request('DELETE', `/maintenance/schedules/${encodeURIComponent(id)}`);
    }
    listShotReadings(query = '') {
        return this.request('GET', `/maintenance/shot-readings${query}`);
    }
    createShotReading(input) {
        return this.request('POST', '/maintenance/shot-readings', input);
    }
    listFailureReasons() {
        return this.request('GET', '/maintenance/failure-reasons');
    }
    // --- Audit, stock take and reports (v5) ---
    listNameplateDesigns() {
        return this.request('GET', '/moulds/nameplate-designs');
    }
    /** Which moulds' engrave files (DXF) were downloaded: count, last time, last person. */
    listEngraveDownloads(mouldId) {
        return this.request('GET', `/nameplates/downloads${mouldId ? `?mouldId=${encodeURIComponent(mouldId)}` : ''}`);
    }
    listActivity(query = '') {
        return this.request('GET', `/audit/activity${query}`);
    }
    listAuditLog(query = '') {
        return this.request('GET', `/audit/log${query}`);
    }
    listStockTakes(query = '') {
        return this.request('GET', `/audit/stock-takes${query}`);
    }
    getStockTake(idOrRef) {
        return this.request('GET', `/audit/stock-takes/${encodeURIComponent(idOrRef)}`);
    }
    startStockTake(locationId) {
        return this.request('POST', '/audit/stock-takes', { locationId });
    }
    scanStockTake(idOrRef, assetCode) {
        return this.request('POST', `/audit/stock-takes/${encodeURIComponent(idOrRef)}/scan`, { assetCode });
    }
    closeStockTake(idOrRef, notes) {
        return this.request('POST', `/audit/stock-takes/${encodeURIComponent(idOrRef)}/close`, { notes: notes ?? null });
    }
    reportSummary(days = 90) {
        return this.request('GET', `/reports/summary?days=${days}`);
    }
    // --- Dashboard ---
    liveBoard() {
        return this.request('GET', '/dashboard/live-board');
    }
}
//# sourceMappingURL=apiClient.js.map